<template>
  <div class="drama-content">
    <div class="full-width-container">
      <!-- 短剧生成列表 -->
      <div class="drama-list">
        <!-- 新生成的短剧（优先显示） -->
        <div class="drama-item" v-for="(item, index) in newDramaItems" :key="'new-' + index">
          <!-- 徽章和时间 -->
          <div class="drama-header">
            <div class="drama-info-badges">
              <div class="info-badge">
                {{ item.duration }}
              </div>
              <div class="type-badge">
                {{ item.genre }}
              </div>
              <div class="button-tag">
                {{ getButtonTag(item.type) }}
              </div>
            </div>
            <div class="generate-time">
              {{ item.generateTime }}
            </div>
          </div>
          
          <!-- 短剧内容布局 - 左右结构 -->
          <div class="drama-layout">
            <!-- 左边：视频区域 -->
            <div class="video-area">
              <div class="video-container">
                <div class="video-wrapper">
                  <!-- 视频播放 -->
                  <video v-if="item.videoUrl" :src="item.videoUrl" controls poster="item.thumbnail" class="video-playback" />
                  
                  <!-- 视频预览占位符 -->
                  <div v-else class="video-placeholder">
                    <div class="placeholder-content">
                      <div class="placeholder-icon">🎬</div>
                      <div class="placeholder-text">短剧视频</div>
                      <div class="placeholder-hint">生成中...</div>
                    </div>
                    
                    <!-- 玻璃折射效果 -->
                    <div class="glass-reflection"></div>
                  </div>
                </div>
                
                <!-- 进度条 -->
                <div class="progress-bar" v-if="item.generating">
                  <div class="progress-fill" :style="{ width: item.progress + '%' }"></div>
                </div>
              </div>
            </div>
            
            <!-- 右边：剧本区域 -->
            <div class="script-area">
              <div class="script-container">
                <div class="script-label">📝 剧本</div>
                <div class="script-content">{{ item.script }}</div>
              </div>
            </div>
          </div>
          
          <!-- 操作按钮 -->
          <div class="drama-actions">
            <button class="action-btn" @click="copyScript(item.script)" title="复制剧本">
              <i class="icon">📋</i>
            </button>
            <button class="action-btn" @click="regenerateDrama(index)" title="重新生成">
              <i class="icon">🔄</i>
            </button>
            <button class="action-btn" @click="editDrama(index)" title="编辑">
              <i class="icon">✏️</i>
            </button>
            <button class="action-btn" @click="downloadVideo(item)" title="下载">
              <i class="icon">⬇️</i>
            </button>
            <button class="action-btn delete" @click="deleteDrama(index, true)" title="删除">
              <i class="icon">🗑️</i>
            </button>
          </div>
        </div>
        
        <!-- 历史生成的短剧（后置加载） -->
        <div class="drama-item" v-for="(item, index) in historyDramaItems" :key="'history-' + item.id">
          <!-- 徽章和时间 -->
          <div class="drama-header">
            <div class="drama-info-badges">
              <div class="info-badge">
                {{ item.duration }}
              </div>
              <div class="type-badge">
                {{ item.genre }}
              </div>
              <div class="button-tag">
                {{ getButtonTag(item.type) }}
              </div>
            </div>
            <div class="generate-time">
              {{ item.generateTime }}
            </div>
          </div>
          
          <!-- 短剧内容布局 - 左右结构 -->
          <div class="drama-layout">
            <!-- 左边：视频区域 -->
            <div class="video-area">
              <div class="video-container">
                <div class="video-wrapper">
                  <!-- 视频播放 -->
                  <video v-if="item.videoUrl" :src="item.videoUrl" controls poster="item.thumbnail" class="video-playback" />
                  
                  <!-- 视频预览占位符 -->
                  <div v-else class="video-placeholder">
                    <div class="placeholder-content">
                      <div class="placeholder-icon">🎬</div>
                      <div class="placeholder-text">短剧视频</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- 右边：剧本区域 -->
            <div class="script-area">
              <div class="script-container">
                <div class="script-label">📝 剧本</div>
                <div class="script-content">{{ item.script }}</div>
              </div>
            </div>
          </div>
          
          <!-- 操作按钮 -->
          <div class="drama-actions">
            <button class="action-btn" @click="copyScript(item.script)" title="复制剧本">
              <i class="icon">📋</i>
            </button>
            <button class="action-btn" @click="regenerateDrama(index, false)" title="重新生成">
              <i class="icon">🔄</i>
            </button>
            <button class="action-btn" @click="editDrama(index, false)" title="编辑">
              <i class="icon">✏️</i>
            </button>
            <button class="action-btn" @click="downloadVideo(item)" title="下载">
              <i class="icon">⬇️</i>
            </button>
            <button class="action-btn delete" @click="deleteDrama(index, false)" title="删除">
              <i class="icon">🗑️</i>
            </button>
          </div>
        </div>
        
        <!-- 空状态 -->
        <div v-if="newDramaItems.length === 0 && historyDramaItems.length === 0 && !isLoadingHistory" class="empty-state">
          <div class="empty-icon">🎭</div>
          <h3>还没有生成过短剧</h3>
          <p>使用下面的工具栏开始创作您的第一个短剧吧！</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'DramaContent',
  props: {
    params: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      newDramaItems: [],
      historyDramaItems: [],
      isLoadingHistory: false,
      isLoaded: false
    }
  },
  mounted() {
    this.loadHistory()
  },
  watch: {
    params: {
      handler(newParams) {
        if (newParams && Object.keys(newParams).length > 0) {
          this.generateFromParams(newParams)
        }
      },
      deep: true
    }
  },
  methods: {
    async loadHistory() {
      if (this.isLoaded) return
      this.isLoaded = true
      
      // 延迟加载历史数据，让新生成的内容优先显示
      await new Promise(resolve => setTimeout(resolve, 500))
      
      this.isLoadingHistory = true
      
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}')
      const user_id = userInfo.id || 0
      
      try {
        const response = await axios.get('/api/drama-history', {
          params: { user_id: user_id }
        })
        
        // 处理响应数据 - 可能是直接数组或包装在响应中
        let data = response.data
        if (data && data.data) {
          data = data.data
        }
        
        // 确保 data 是数组
        if (!Array.isArray(data)) {
          data = []
        }
        
        // 将后端数据格式转换为前端期望的格式，存入历史列表
        this.historyDramaItems = data.map(item => ({
          id: item.id,
          type: 'text-to-drama',
          prompt: item.title || '',
          duration: item.duration || '',
          genre: item.style || '剧情',
          script: item.script || '',
          videoUrl: '',
          thumbnail: '',
          generating: false,
          progress: 0,
          generateTime: item.createdAt || item.created_at || ''
        }))
      } catch (error) {
        console.error('加载短剧历史失败:', error)
        this.historyDramaItems = []
      } finally {
        this.isLoadingHistory = false
      }
    },
    generateFromParams(params) {
      // 转换类型名称
      let dramaType = 'text-to-drama'
      if (params.dramaType) {
        if (params.dramaType === 'normal') {
          dramaType = 'text-to-drama'
        } else if (params.dramaType === '2d') {
          dramaType = '2d-animation'
        } else if (params.dramaType === 'cg') {
          dramaType = 'cg-animation'
        } else {
          dramaType = params.dramaType
        }
      }
      
      // 准备生成的数据
      const newDrama = {
        type: dramaType,
        prompt: params.prompt || '',
        duration: params.duration || '3分钟',
        genre: params.genre || '剧情',
        script: params.prompt || '剧本内容...',
        videoUrl: '',
        thumbnail: '',
        generating: false,
        progress: 0,
        generateTime: this.getCurrentTime()
      }
      
      // 添加到新生成列表
      this.newDramaItems.unshift(newDrama)
    },
    getCurrentTime() {
      const now = new Date()
      const year = now.getFullYear()
      const month = String(now.getMonth() + 1).padStart(2, '0')
      const day = String(now.getDate()).padStart(2, '0')
      const hours = String(now.getHours()).padStart(2, '0')
      const minutes = String(now.getMinutes()).padStart(2, '0')
      return `${year}-${month}-${day} ${hours}:${minutes}`
    },
    copyScript(script) {
      if (!script) {
        alert('暂无剧本文本')
        return
      }
      navigator.clipboard.writeText(script).then(() => {
        alert('剧本已复制到剪贴板')
      })
    },
    
    regenerateDrama(index, isNew) {
      const item = isNew ? this.newDramaItems[index] : this.historyDramaItems[index]
      console.log('重新生成:', item)
    },
    
    editDrama(index, isNew) {
      const item = isNew ? this.newDramaItems[index] : this.historyDramaItems[index]
      console.log('编辑:', item)
    },
    
    downloadVideo(item) {
      console.log('下载:', item)
      if (item.videoUrl) {
        const link = document.createElement('a')
        link.href = item.videoUrl
        link.download = `drama-${item.generateTime}.mp4`
        link.click()
      } else {
        alert('视频还在生成中或下载链接无效')
      }
    },
    
    async deleteDrama(index, isNew) {
      if (!confirm('确定要删除这个短剧吗？')) {
        return
      }
      
      try {
        if (isNew) {
          this.newDramaItems.splice(index, 1)
          console.log('✅ 已删除新生成的短剧')
        } else {
          const item = this.historyDramaItems[index]
          console.log('🗑️ 准备删除短剧:', item)
          
          if (item.id) {
            const response = await fetch(`/api/drama-history/${item.id}`, {
              method: 'DELETE',
              headers: { 'Content-Type': 'application/json' }
            })
            
            const data = await response.json()
            console.log('📤 删除响应:', data)
            
            if (data.code === 200) {
              console.log('✅ 数据库删除成功')
            } else {
              console.warn('⚠️ 数据库删除失败，但继续移除前端显示')
            }
          }
          
          this.historyDramaItems.splice(index, 1)
        }
      } catch (error) {
        console.error('❌ 删除失败:', error)
        alert('删除失败，请重试')
      }
    },
    
    addNewDrama(newDrama) {
      this.newDramaItems.unshift(newDrama)
    },
    
    getButtonTag(type) {
      const tagMap = {
        'text-to-video': '文生视频',
        'image-to-video': '图生视频',
        'digital-human': '数字人',
        'text-to-drama': '常规短剧',
        '2d-animation': '2D动画',
        'cg-animation': 'CG动漫'
      }
      return tagMap[type] || '常规短剧'
    }
  }
}
</script>

<style scoped>
.drama-content {
  width: 100%;
}

.full-width-container {
  max-width: 1400px;
  margin: 0 auto;
  width: 100%;
  padding: 20px 30px 20px 30px;
}

/* 短剧列表 */
.drama-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.drama-item {
  background: rgba(255, 255, 255, 0.03);
  border: none;
  border-radius: 16px;
  padding: 16px;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  box-sizing: border-box;
}

.drama-item:hover {
  box-shadow: 0 8px 32px rgba(0, 230, 172, 0.1);
}

.drama-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 10px;
}

.drama-info-badges {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.info-badge {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: #e6e6e6;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 12px;
}

.type-badge {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: #e6e6e6;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.button-tag {
  background: rgba(0, 230, 172, 0.15);
  border: 1px solid rgba(0, 230, 172, 0.4);
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 12px;
  color: #00e6ac;
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
}

.generate-time {
  color: #aaa;
  font-size: 12px;
  white-space: nowrap;
}

/* 短剧内容布局 - 左右结构 */
.drama-layout {
  display: flex;
  gap: 20px;
  margin-bottom: 16px;
  min-height: 400px;
}


/* 左边：视频区域 */
.video-area {
  flex: 1.5;
  min-width: 0;
}

/* 右边：剧本区域 */
.script-area {
  flex: 1;
  min-width: 0;
}

.video-container {
  position: relative;
  background: rgba(255, 255, 255, 0.02);
  border: none;
  border-radius: 12px;
  overflow: hidden;
  height: 100%;
  min-height: 350px;
  display: flex;
  flex-direction: column;
}


.script-container {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 12px;
  height: 100%;
  min-height: 350px;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
}

.video-wrapper {
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.03);
}

.video-playback {
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #000;
}

.video-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
}

.placeholder-content {
  text-align: center;
  z-index: 10;
  color: rgba(255, 255, 255, 0.4);
}

.placeholder-icon {
  font-size: 64px;
  margin-bottom: 12px;
}

.placeholder-text {
  font-size: 18px;
  margin-bottom: 4px;
}

.placeholder-hint {
  font-size: 14px;
  color: #495670;
}

/* 玻璃折射效果 */
.glass-reflection {
  position: absolute;
  top: 0;
  left: -50%;
  width: 200%;
  height: 100%;
  background: linear-gradient(
    105deg,
    transparent 40%,
    rgba(255, 255, 255, 0.1) 45%,
    rgba(255, 255, 255, 0.2) 50%,
    rgba(255, 255, 255, 0.1) 55%,
    transparent 60%
  );
  animation: shine 3s infinite;
  pointer-events: none;
}

@keyframes shine {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}

/* 进度条 */
.progress-bar {
  height: 4px;
  background: rgba(255, 255, 255, 0.1);
  position: relative;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #00e6ac, #00c292);
  position: relative;
  transition: width 0.3s ease;
}

.progress-fill::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.4),
    transparent
  );
  animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}


.script-label {
  color: #00e6ac;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 8px;
}

.script-content {
  font-size: 14px;
  line-height: 1.6;
  color: #e6e6e6;
  white-space: pre-wrap;
  flex: 1;
  overflow-y: auto;
  margin-top: 8px;
}

/* 操作按钮 */
.drama-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  padding-top: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.action-btn {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 8px 12px;
  color: #aaa;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.action-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(0, 230, 172, 0.3);
  color: #00e6ac;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 230, 172, 0.2);
}

.action-btn.delete {
  color: #aaa;
  border-color: rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
}

.action-btn.delete:hover {
  color: #ff6b6b;
  border-color: rgba(255, 107, 107, 0.3);
  background: rgba(255, 107, 107, 0.1);
}

/* 空状态 */
.empty-state {
  text-align: center;
  padding: 80px 20px;
  color: #8892b0;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.5;
}

.empty-state h3 {
  font-size: 20px;
  margin-bottom: 8px;
  color: #e6f1ff;
}

.empty-state p {
  font-size: 16px;
  max-width: 400px;
  margin: 0 auto;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .full-width-container {
    padding: 20px 16px;
  }
  
  .drama-layout {
    flex-direction: column;
    min-height: auto;
  }
  
  .drama-actions {
    flex-wrap: wrap;
  }
}
</style>
